import * as angular from "angular"
import { selectMany } from "../utils/array"
import { isArray, isObject, isUndefinedOrNull } from "../utils/utils"
import { Layer } from "./layer"
import { IMapMeasuringService } from "./measuring/types"
import { getDefaultEditStyleFunction } from "./styles/defaults"
import { IMapAutoEditConfig, OpenLayersStyleProvider } from "./types"
import { setFeatureStyle } from "./utils"
import { ol_polygonFromCircle, ol_geom, ol_GeometryType, ol_VectorLayer, ol_layer, ol_Feature, ol_style, ol_interaction, ol_Map, ol_Collection, ol_Observable_unByKey, ol_events_condition } from "@omnia/exports/openlayers";
import { createRegularPolygon } from "ol/interaction/Draw"

/** omnia-i18n-ignore-strings */
export class MapEditInteraction {

    private map: ol_Map;
    private _clickWatcherKey: any;

    private features = new ol_Collection<ol_Feature>();

    private originalFeature: ol_Feature;
    private currentFeature: ol_Feature;

    private editMode: boolean = false;
    private drawingMode: boolean = false;
    private drawingType: string;

    private _autoEdit: IMapAutoEditConfig;

    private newFeature: ol_Feature;
    private newFeatureLayer: ol_VectorLayer;

    private newFeatureDrawStartWatcher: (feature: ol_Feature) => void;
    private newFeatureDrawEndWatcher: (feature: ol_Feature) => void;

    private modifyInteraction: ol_interaction.Modify;
    private drawInteraction: ol_interaction.Draw;
    private drawInteractionEventKeys: {
        start: any,
        end: any
    };

    private snapInteraction: ol_interaction.Snap;
    private snapFeatures: ol_Collection<ol_Feature>;

    private translateInteraction: ol_interaction.Translate;
    private translateFeatures: ol_Collection<ol_Feature>;

    private snapLayers: Layer[];


    constructor(
        olmap: ol_Map,
        private selectInteraction: {
            active: boolean,
            clearSelection(): void
        },
        private timeout: (fn: () => void) => void,
        private measure: IMapMeasuringService
    ) {

        this.map = olmap;
        this.setup();
    }

    dispose() {

        if (this.map) {

            ol_Observable_unByKey(this._clickWatcherKey);

            this.map.removeInteraction(this.modifyInteraction);
            this.map.removeInteraction(this.translateInteraction);
            this.map.removeInteraction(this.snapInteraction);

            this.disposeDrawInteraction();

            this.map = null;

        }

    }

    private disposeDrawInteraction() {

        if (this.drawInteraction) {

            this.map.removeInteraction(this.drawInteraction);

            let keys = this.drawInteractionEventKeys;
            if (keys) {
                ol_Observable_unByKey(this.drawInteractionEventKeys.start);
                ol_Observable_unByKey(this.drawInteractionEventKeys.end);
            }

            this.drawInteraction = null;
            this.drawInteractionEventKeys = null;
        }

    }

    private setup() {

        this.translateFeatures = new ol_Collection<ol_Feature>();
        this.translateInteraction = new ol_interaction.Translate(<any>{
            features: this.translateFeatures,
            hitTolerance: 5
        });
        this.map.addInteraction(this.translateInteraction);

        this.modifyInteraction = new ol_interaction.Modify({
            features: this.features,
            deleteCondition: ol_events_condition.click
        });
        this.map.addInteraction(this.modifyInteraction);

        this.snapFeatures = new ol_Collection<ol_Feature>();
        this.snapInteraction = new ol_interaction.Snap({
            features: this.snapFeatures,
        });
        this.map.addInteraction(this.snapInteraction);

        this._clickWatcherKey = this.map.on("click", event => this.onClick(event));

    }

    private setupDrawInteraction(type: ol_GeometryType | "Square" | "Box", style?: any) {

        var maxPoints;
        var geometryFunction;

        if (this.drawInteraction) {
            this.disposeDrawInteraction();
            this.map.removeInteraction(this.snapInteraction);
        }

        if (type == "Square") {
            type = "Circle";
            geometryFunction = createRegularPolygon(4);
        }
        else if (type == "Box") {
            type = "LineString";
            maxPoints = 2;
            geometryFunction = boxGeometryFunction;
        }

        this.drawInteraction = new ol_interaction.Draw({
            type: type,
            maxPoints: maxPoints,
            geometryFunction: geometryFunction,
            style: style
        });

        let startKey = this.drawInteraction.on("drawstart", event => this.onDrawStart(event));
        let endKey = this.drawInteraction.on("drawend", event => this.onDrawEnd(event));

        this.drawInteractionEventKeys = {
            start: startKey,
            end: endKey
        };

        this.drawInteraction.setActive(true);

        this.map.addInteraction(this.drawInteraction);
        this.map.addInteraction(this.snapInteraction);

    }

    getEditMode(): boolean {
        return this.editMode
    }

    getDrawingMode(): boolean {
        return this.drawingMode
    }

    isActive() {
        return this.editMode || this.drawInteraction
    }

    startEdit(feature: ol_Feature) {

        if ((!feature) || this.editMode) return false;

        this.selectInteraction.active = false;

        this.currentFeature = feature;
        this.originalFeature = feature.clone();

        setFeatureStyle(feature, this.getEditStyleFunction(feature));

        this.features.clear();
        this.features.push(feature);

        this.setupSnappingSource(true);

        this.editMode = true;

        return true;

    }

    endEdit(cancel) {

        if (cancel && this.currentFeature) {
            this.currentFeature.setGeometry(
                this.originalFeature.getGeometry()
            );
        }

        this.resetModifiedFeatures();

        this.setupSnappingSource(false);

        this.translateFeatures.clear();

        this.selectInteraction.active = true;

        this.currentFeature = null;
        this.originalFeature = null;

        this.editMode = false;

        return true;

    }

    cancelAllEdits() {

        if (this.editMode) {
            this.endEdit(true);
        }

        if (this.drawingMode) {
            this.endDrawing(true);
        }

    }

    startDrawing(targetLayer: any, type: ol_GeometryType | "Box" | "Square" = "Polygon", options?: {
        drawStartWatcher?: (feature: ol_Feature) => void,
        drawEndWatcher?: (feature: ol_Feature) => void,
        style?: ol_style.Style[] | OpenLayersStyleProvider,
        keepSelection?: boolean
        moveLayerToTop?: boolean
    }) {

        options = options || {};

        this.resetModifiedFeatures();

        if (!options.keepSelection) {
            this.selectInteraction.clearSelection();
        }
        this.selectInteraction.active = false;

        this.translateFeatures.clear();

        this.setupDrawInteraction(type, options.style);

        this.setupSnappingSource(true);

        if (targetLayer) {

            if (isUndefinedOrNull(options.moveLayerToTop) ? true : options.moveLayerToTop) {
                // The target layer is moved to the top of the stack,
                // so as to make sure that the feature being drawn will be visible
                // TODO: should be done only if necessary
                this.map.removeLayer(targetLayer);
                this.map.addLayer(targetLayer);
            }

            this.newFeatureLayer = targetLayer;

        }

        this.drawingMode = true;
        this.drawingType = type;

        this.newFeatureDrawStartWatcher = options.drawStartWatcher;
        this.newFeatureDrawEndWatcher = options.drawEndWatcher;

        return true;

    }

    endDrawing(cancel) {

        const { drawInteraction, newFeatureLayer, newFeature } = this

        if (!drawInteraction) return;

        if (!cancel && drawInteraction.getActive()) {
            drawInteraction.finishDrawing();
        }

        if (cancel && newFeatureLayer && newFeature) {

            try {
                newFeatureLayer.getSource().removeFeature(newFeature)
            } catch (error) {
                console.error(error)
            }

        }

        this.resetModifiedFeatures();

        this.translateFeatures.clear();

        this.drawInteraction.setActive(false);

        this.setupSnappingSource(false);

        this.selectInteraction.active = true;

        this.drawingMode = false;
        this.editMode = false;

        this.newFeature = null;

        return newFeature;

    }

    resetModifiedFeatures() {

        angular.forEach(this.features, f => {
            f.setStyle(null);
        });

        this.features.clear();

    }

    canCommit() {

        if (this.drawingMode) {

            if (!this.newFeature) return false;

            var g: any = this.newFeature.getGeometry();

            switch (this.drawingType) {

                case "Point":
                    return true;

                case "Circle":
                    return true;

                case "LineString":
                    return (g.getLength && (g.getLength() > 0));

                default:
                    return (g.getArea && (g.getArea() > 0))


            }

        }

        return true;

    }

    getEditedFeatureArea(): number {

        if (this.editMode || this.drawingMode) {

            let f = this.newFeature || this.currentFeature;

            if (f) {
                return this.measure.getArea(f)
            }

        }

    }

    getNewFeatureArea(): number {

        if (this.drawingMode && this.newFeature) {
            return this.measure.getArea(this.newFeature)
        }

    }

    onDrawStart(event: { feature: ol_Feature }) {

        this.newFeature = event.feature;

        if (this.newFeatureDrawStartWatcher) {
            this.newFeatureDrawStartWatcher(this.newFeature);
            this.newFeatureDrawStartWatcher = null;
        }

    }

    onDrawEnd(event) {

        const feature = event.feature
        let geom = feature.getGeometry()

        if (geom.getType() == "Circle") {
            geom = ol_polygonFromCircle(geom)
            feature.setGeometry(geom)
        }

        setFeatureStyle(feature, this.getEditStyleFunction(feature))

        this.newFeatureLayer.getSource().addFeature(feature)
        this.features.push(feature)

        this.translateFeatures.push(feature)

        this.timeout(() => {

            this.editMode = true;

            this.drawInteraction.setActive(false);

            if (this.newFeatureDrawEndWatcher) {
                this.newFeatureDrawEndWatcher(this.newFeature);
                this.newFeatureDrawEndWatcher = null;
            }

        });

    }


    autoEdit(config: IMapAutoEditConfig) {

        this._autoEdit = config;

    }

    private onClick(event) {

        var autoEdit = this._autoEdit;

        if (!(autoEdit && autoEdit.layer)) return;

        this.map.forEachFeatureAtPixel(event.pixel, (feature: ol_Feature, layer: ol_layer.Layer) => {

            if (layer == autoEdit.layer.mapLayer) {

                this.autoEditSelectedFeature(autoEdit, feature);
                return true;
            }

        });

    }

    private autoEditSelectedFeature(autoEdit: IMapAutoEditConfig, feature): boolean {

        if (this.drawInteraction && this.drawInteraction.getActive()) {
            return;
        }

        if (this.editMode && (this.currentFeature == feature)) {
            return;
        }

        if (autoEdit.watcher) {

            if (!autoEdit.watcher(feature)) {
                return false;
            }

        }

        if (this.drawingMode || this.editMode) {

            if (!this.canCommit()) {
                return;
            }

            if (this.drawingMode) {
                this.endDrawing(false);
            }
            else if (this.editMode) {
                this.endEdit(false);
            }

        }

        this.startEdit(feature);

        if (autoEdit.translate) {
            this.translateFeatures.clear();
            this.translateFeatures.push(feature);
        }

        return true;

    }

    setSnappingLayers(layers: Layer[]) {

        this.snapLayers = layers

        if (this.isActive()) {
            this.setupSnappingSource(true)
        }

    }

    private setupSnappingSource(start: boolean) {

        this.snapFeatures.clear();

        if (start && isArray(this.snapLayers)) {

            let features = selectMany(
                this.snapLayers.map(l => l.getSnappingFeatures()).filter(f => !!f)
            );

            this.snapFeatures.extend(features);

            this.snapInteraction.setActive(true);

        } else {

            this.snapInteraction.setActive(false);

        }

    }

    private getEditStyleFunction(feature: ol_Feature) {

        var layer = <Layer>feature.get("layer");

        if (isObject(layer) && layer.getEditStyleFunction) {
            return layer.getEditStyleFunction(feature);
        }

        return getDefaultEditStyleFunction(feature);

    }

    refresh() {

        if (this.editMode && this.currentFeature) {

            setFeatureStyle(this.currentFeature, this.getEditStyleFunction(this.currentFeature))

        }
    }

    public setTranslateFeatures(features: ol_Feature[]) {

        this.translateFeatures.clear();

        if (isArray(features) && features.length) {
            this.translateFeatures.extend(features);
            this.setupSnappingSource(true);
        }

    }

}

function boxGeometryFunction(coordinates: number[][], geometry: any) {

    function getCoordinates() {
        const start = coordinates[0]
        const end = coordinates[1]
        return [[start, [start[0], end[1]], end, [end[0], start[1]], start]]
    }

    if (!geometry) {
        geometry = new ol_geom.Polygon(getCoordinates())
    }
    else {
        geometry.setCoordinates(getCoordinates())
    }

    return geometry

}

